import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  Input,
  OnInit,
  Optional,
} from '@angular/core';
import {
  AsyncPipe,
  DOCUMENT,
  NgClass,
  NgComponentOutlet,
  NgOptimizedImage,
  NgStyle,
  NgTemplateOutlet,
  TitleCasePipe,
} from '@angular/common';
import {
  ContainerItemModel,
  ContainerModel,
} from '@app/models/server/layout.model';
import { B2bSliderItemComponent } from '@app/new-ui-lib/b2b-sliders/components/b2b-slider-item/b2b-slider-item.component';
import { B2bDragScrollComponent } from '@app/new-ui-lib/b2b-drag-scroll/b2b-drag-scroll/b2b-drag-scroll.component';
import { B2bDragScrollItemComponent } from '@app/new-ui-lib/b2b-drag-scroll/b2b-drag-scroll-item/b2b-drag-scroll-item.component';
import { DragScrollType } from '@app/models/client/drag-scroll.type';
import { COMP_STATES } from '@app/constants/app-settings';
import { catchError, Observable, of, switchMap } from 'rxjs';
import { HttpClientService } from '@app/services/util-services/root-services/http-client.service';
import { B2bDeviceUtilService } from '@app/services/util-services/root-services/b2b-device-util.service';
import { B2bPopoverV1Component } from '@app/new-ui-lib/b2b-popover-v1/b2b-popover-v1.component';
import { environment } from '@src/environments/environment';
import { B2bSlicePipe } from '@app/components/pipes/b2b-slice/b2b-slice.pipe';
import { TextClampShellComponent } from '@app/components/ui-libs/shell/text-clamp-shell/text-clamp-shell.component';
import { Router } from '@angular/router';
import { B2bDynamicLoaderPipe } from '@app/b2b-dynamic-lib/pipes/b2b-dynamic-loader-pipe.pipe';
import {TapEffect} from '@app/components/directives/tap-effect.ts/tap-effect';

@Component({
  selector: 'app-b2b-sliders',
  templateUrl: './b2b-sliders.component.html',
  styleUrls: ['./b2b-sliders.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TitleCasePipe,
    B2bSliderItemComponent,
    B2bDragScrollComponent,
    B2bDragScrollItemComponent,
    AsyncPipe,
    NgClass,
    NgTemplateOutlet,
    B2bPopoverV1Component,
    NgOptimizedImage,
    B2bSlicePipe,
    TextClampShellComponent,
    NgStyle,
    B2bDynamicLoaderPipe,
    NgComponentOutlet,
    TapEffect,
  ],
  schemas: [],
})
export class B2bSlidersComponent implements OnInit {
  @Input() compData: {
    container: ContainerModel;
    loaded?: (value: any) => void;
  };

  @Input() config: DragScrollType;
  showScrollContainer = false;
  isMobile: boolean =
    environment?.mobileApp || !this.b2bDeviceUtilService.isDesktop();
  slidersData$: Observable<{
    originalContainer: ContainerModel;
    finalContainers: ContainerModel[];
    finalContainerItems: ContainerItemModel[];
    selectedContainer?: ContainerModel;
  }>;

  hostWebsite: string = environment.hostWebsite;

  constructor(
    private httpClientService: HttpClientService,
    @Inject(DOCUMENT) private readonly document,
    private b2bDeviceUtilService: B2bDeviceUtilService,
    @Optional() @Inject('WINDOW') private window: any,
    private router: Router,
  ) {}

  ngOnInit() {
    this.initApi();
  }

  initApi() {
    if (this.compData?.container?.apiURL) {
      this.slidersData$ = this.httpClientService
        ?.getDynamicContainersData(
          this.compData?.container?.apiURL,
          this.compData?.container?.isCacheEnabled,
          this.compData?.container?.key,
          this.compData?.container?.apiRequestBody,
          null,
          true,
        )
        .pipe(
          switchMap((containers: ContainerModel[]) => {
            if (containers?.length === 1) {
              if (this.compData?.container?.type === 'OFFER_SLIDER') {
                return of(this.offerSliderSetup(containers[0]));
              } else {
                return of(this.afterInitApi(containers[0]));
              }
            } else {
              this.delete();
              return of({
                originalContainer: null,
                finalContainers: [],
                finalContainerItems: [],
              });
            }
          }),
          catchError((e) => {
            this.delete();

            return of({
              originalContainer: null,
              finalContainers: [],
              finalContainerItems: [],
            });
          }),
        );
    } else {
      this.slidersData$ = of(this.afterInitApi(this.compData?.container));
    }
  }

  delete() {
    if (!this.b2bDeviceUtilService?.isServer()) {
      const element = this.document?.getElementById(
        this.compData?.container?.key,
      );

      if (element) {
        element.remove();
      }
    }
  }

  offerSliderSetup(container: ContainerModel): {
    originalContainer: ContainerModel;
    finalContainers: ContainerModel[];
    finalContainerItems: ContainerItemModel[];
    selectedContainer?: ContainerModel;
  } {
    let selectedContainer: ContainerModel;
    let finalContainers: ContainerModel[];

    if (container?.containers?.length > 0) {
      selectedContainer = container?.containers?.[0];
      finalContainers = [...container?.containers];

      this.config = this.getConfig(container);
      this.showScrollContainer = true;

      if (this.compData?.loaded) {
        this.compData?.loaded(COMP_STATES?.LOADED);
      }
      return {
        originalContainer: container,
        finalContainers,
        finalContainerItems: undefined,
        selectedContainer,
      };
    } else {
      this.delete();
      return {
        originalContainer: container,
        finalContainers: undefined,
        finalContainerItems: undefined,
        selectedContainer: undefined,
      };
    }
  }

  afterInitApi(container: ContainerModel): {
    originalContainer: ContainerModel;
    finalContainers: ContainerModel[];
    finalContainerItems: ContainerItemModel[];
    selectedContainer?: ContainerModel;
  } {
    let finalContainers: ContainerModel[];
    let finalContainerItems: ContainerItemModel[];
    if (container?.containers?.length > 0) {
      finalContainers = [...container?.containers];
    } else if (container?.data?.length > 0) {
      finalContainerItems = [...container?.data];
    }
    this.config = this.getConfig(container);

    this.showScrollContainer = true;

    if (this.compData?.loaded) {
      this.compData?.loaded(COMP_STATES?.LOADED);
    }

    if (finalContainers?.length > 0 || finalContainerItems?.length > 0) {
      return {
        originalContainer: container,
        finalContainers,
        finalContainerItems,
      };
    } else {
      this.delete();
      return {
        originalContainer: container,
        finalContainers,
        finalContainerItems,
      };
    }
  }

  getConfig(container: ContainerModel): DragScrollType {
    return {
      autoHeight: true,
      spaceBetween:
        container?.spaceBetween > 0 ? container?.spaceBetween : null,
      navigation: true,
      freeMode: false,
      mousewheel: false,
      grabCursor: false,
      pagination: true,
      slidesPerView: container?.slidesInView > 0 ? container?.slidesInView : 1,
      centeredSlides: false,
      injectStyles: [],
      hideScrollButtons: container?.hideScrollButtons,
      showAbsoluteButtons: container?.showAbsoluteButtons,
      background: container?.background,
      divider: container?.divider,
      cardType: container?.cardType,
    };
  }

  onCardClick(slide: ContainerModel) {
    const navigationURL: string = slide?.navigationURL;
    const externalUrl: string = slide?.externalUrl;

    const finalUrl: string = externalUrl ? externalUrl : navigationURL;

    if (externalUrl) {
      this.b2bDeviceUtilService
        .openSite(
          externalUrl,
          this.b2bDeviceUtilService?.isDesktop() ? '_blank' : '_self',
        )
        .then((r) => {});
      return;
    } else {
      if (this.b2bDeviceUtilService?.isDesktop()) {
        this.window?.open(this.window?.origin + finalUrl, '_blank');
      } else {
        this.router.navigateByUrl(finalUrl).then();
      }
    }
  }

  ngOnDestroy() {}
}
